/* istanbul ignore file */
import { InfoBox } from "~/components/Backroom/InfoBox";
import Input from "~/components/Backroom/Input";
import Button from "~/components/Button";
import { Checkbox } from "~/components/Backroom/Checkbox";
import Icon from "~/assets/icons/Icon";
import { CurrentStepProps } from "../getSideMenuItems";
import InfoModal from "../../Infomodal";
import { MouseEvent, useEffect } from "react";
import { Inputs } from "../useUpsertProductForm";
import { RichTextInput } from "../../RichTextInput";

export const descriptionPreviewImages = [
  {
    id: "description1",
    title: "Vist på produktsiden",
    src: "/assets/images/previews/products/describe-product/desktop.png",
    alt: "Placering af teksten på produktsiden",
    width: 340,
    height: 420,
  },
  {
    id: "description2",
    title: "Vist på produktsiden på mobil",
    src: "/assets/images/previews/products/describe-product/mobil.png",
    alt: "Placering af teksten på produktsiden på mobil",
    width: 200,
    height: 500,
  },
  {
    id: "description3",
    title: "Vist på produktoverbliksiden",
    src: "/assets/images/previews/products/describe-product/desktop2.png",
    alt: "Placering af teksten på produktoverbliksiden",
    width: 340,
    height: 420,
  },
  {
    id: "description4",
    title: "Vist på produktoverbliksiden på mobil",
    src: "/assets/images/previews/products/describe-product/mobil2.png",
    alt: "Placering af teksten på produktoverbliksiden på mobil",
    width: 200,
    height: 500,
  },
];

export const FIELDS: Array<keyof Inputs> = [
  "name",
  "description",
  "danishDesign",
  "danishProduction",
  "danishMaterials",
];

export const DescriptionStep = (props: CurrentStepProps) => {
  const {
    stepId,
    form: {
      register,
      control,
      formState: { errors, isDirty },
      reset,
      setValue,
      setError,
      clearErrors,
      trigger,
      watch,
      getValues,
    },
    onContinue,
    isSubmitting,
    isEditing,
    onStepChange,
  } = props;
  const danishDesign = watch("danishDesign");

  const beforeContinue = async (
    _event?: MouseEvent,
    options?: { shouldExitEditing?: boolean }
  ) => {
    const isValid = await trigger(FIELDS);

    if (isValid) {
      onContinue(stepId, {
        visibleFields: FIELDS,
        shouldExitEditing: options?.shouldExitEditing,
      });
      clearErrors(["danishDesign"]);
      // istanbul ignore next
      isEditing && reset({}, { keepValues: true });
    }
  };

  useEffect(() => {
    // We have to check for false, because it goes undefined for a sec when continuing
    if (danishDesign === false) {
      setValue("danishDesign", true);
      setError("danishDesign", {
        type: "manual",
        message: "Det er et krav, at designet er dit eget",
      });
    }
  }, [danishDesign]);

  useEffect;

  return (
    <>
      <h1 className="text-3xl mb-5">Beskriv dit produkt</h1>
      <p className="mb-12">
        Start med at give det nye produkt et navn og en beskrivelse. Et godt
        produktnavn signalerer tydeligt, hvad produktet er. En god og personlig
        produktbeskrivelse øger desuden chancen for, at potentielle kunder
        lægger varen i kurven.
      </p>

      <div className="mb-12">
        <Input
          id="name"
          label="Angiv produktets navn:"
          placeholder="Hånddrejet saltkar"
          register={register}
          className="mb-5"
          error={errors.name}
          registerOptions={{ required: "Navn er påkrævet" }}
        />

        <RichTextInput
          id="description"
          label="Beskriv produktet:"
          placeholder="Dette saltkar er hånddrejet i keramik og er perfekt til at opbevare salt i."
          error={errors.description}
          initialValue={getValues("description")??''}
          onChange={(value) => {
            // we added this regex to allow user to leave empty line in the editor
            const newValue = value.replace(/\n{2}(\n{2})+/g, (m) => {
              return m
                .split(/(\n\n)/)
                .filter(Boolean)
                .join("​");
            });
            setValue("description", newValue, { shouldDirty: true });
          }}
          registerOptions={{
            required: "Beskrivelse er påkrævet",
            minLength: {
              value: 8,
              message: "Produkt beskrivelse skal være mindst 8 tegn lang.",
            },
          }}
        />
      </div>

      <div className="mb-12 flex flex-col gap-5">
        <div className="flex">
          <Checkbox
            control={control}
            id="danishDesign"
            label="Dette produkt er dit eget design"
            validation={{ required: "Det er et krav, at designet er dit eget" }}
          ></Checkbox>
          <InfoModal id="danishDesignDescription" label="">
            <p className="mb-8">
              Du bekræfter herved, at produktet er dit eget design. Det vil
              fremgå som en lille label under selve produktet i din butik.
            </p>
          </InfoModal>
        </div>
        <div className="flex">
          <Checkbox
            control={control}
            id="danishProduction"
            label="Dette produkt er produceret i Danmark"
          ></Checkbox>
          <InfoModal id="danishProducedDescription" label="">
            <p className="mb-8">
              Du bekræfter herved, at produktet er produceret i Danmark. Det vil
              fremgå som en lille label under selve produktet i din butik.
            </p>
          </InfoModal>
        </div>
        <div className="flex">
          <Checkbox
            control={control}
            id="danishMaterials"
            label="Dette produkt består hovedsageligt af materialer fra Danmark"
          ></Checkbox>
          <InfoModal id="danishMaterialsDescription" label="">
            <p className="mb-8">
              Du bekræfter herved, at produktet hovedsageligt består af
              materialer, der er produceret i Danmark. Det vil fremgå som en
              lille label under selve produktet i din butik.
            </p>
          </InfoModal>
        </div>
      </div>

      <div className="flex flex-col-reverse lg:flex-col gap-12">
        <InfoBox>
          <ul className="list-disc pl-6">
            <li>Du kan ændre navnet senere.</li>
            <li>Du kan ændre teksten senere.</li>
            <li>Teksten bør ikke være længere end 720 tegn.</li>
            <li>Brug “jeg” eller “vi” for at gøre teksten personlig.</li>
            <li>
              Tilføj emojis 👍😊❤️ til din tekst ved brug af denne genvej på
              MAC: Ctrl + Cmd + Space
            </li>
            <li>
              Tilføj emojis 🤝😍🌈 til din tekst ved brug af denne genvej på PC:
              Windows + .{" "}
            </li>
          </ul>
        </InfoBox>
        <div className={`flex flex-col md:flex-row gap-5`}>
          {!isEditing ? (
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              onClick={beforeContinue}
              disabled={isSubmitting}
            >
              Gem og fortsæt
            </Button>
          ) : (
            <>
              {isEditing && onStepChange && !isDirty ? (
                <Button
                  variant="primary-light"
                  size="lg"
                  className="flex items-center gap-2.5 justify-center w-full md:w-1/4"
                  onClick={() => onStepChange("next")}
                  disabled={isDirty}
                >
                  Næste
                  <Icon name="arrowRight" className="w-6 h-6" />
                </Button>
              ) : (
                <>
                  <Button
                    variant="primary"
                    size="lg"
                    className="w-full md:w-1/3"
                    onClick={beforeContinue}
                    disabled={!isDirty || isSubmitting}
                  >
                    Gem og næste
                  </Button>
                  <Button
                    variant="primary"
                    size="lg"
                    className="w-full md:w-1/3"
                    onClick={(e) =>
                      beforeContinue(e, { shouldExitEditing: true })
                    }
                    disabled={!isDirty || isSubmitting}
                  >
                    Gem og afslut
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};
